import { useEffect, useState } from 'react';

import { Grid, ListSubheader } from '@mui/material';

import { Flipper, Flipped } from 'react-flip-toolkit';

import { useQuery } from '@apollo/client/react';

import { PinnedPostSummary } from 'Modules/group/noticeboard/post-collection/post/pinned-posts-collection/PinnedPostSummary';

import type { DocumentType } from '@/gql';
import { graphql } from '@/gql';
import { extractConnectionProps } from '@/utils/queries/queryExtracts';
import { nonFalsey } from '@/utils/nonFalsey';
import { Post } from '@/modules/group/noticeboard/post-collection/Post';
import { Or2Dialog } from '@/components/feedback/Or2Dialog';

export const PINNED_POSTS_COLLECTION_QUERY = graphql(`
  query PinnedPostsCollection($groupId: ID!) {
    group(id: $groupId) {
      _id
      pinnedPostsConnection: postsConnection(
        first: null
        after: ""
        orderBy: LASTMODIFIED_DESC
        filterBy: { pinned: true }
      ) @connection(key: "noticeboardpinnedposts") {
        edges {
          node {
            ...postFields
            ...postPostFragment
            ...pinnedPostSummaryFragment
          }
        }
      }
    }
  }
`);

const PINNED_POSTS_COLLECTION_GROUP_FRAGMENT = graphql(`
  fragment pinnedPostsCollectionGroupFields on Group {
    ...postGroupFields
    _id
    name
    userCanPost
    userCanDeleteAllPosts
    userCanEditAllPosts
    referenceName
  }
`);

interface Props {
  group: DocumentType<typeof PINNED_POSTS_COLLECTION_GROUP_FRAGMENT>;
  getSubgroupColor: (subgroupId: string) => string;
}

export const PinnedPostsCollection = ({ group, getSubgroupColor }: Props) => {
  const [postDialogOpen, setPostDialogOpen] = useState(false);

  const { _id: groupId } = group;

  const { data } = useQuery(PINNED_POSTS_COLLECTION_QUERY, {
    variables: { groupId: groupId ?? '' },
    skip: !groupId,
  });

  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);

  const pinnedPosts = extractConnectionProps(
    data?.group?.pinnedPostsConnection,
  );

  const flipKey = pinnedPosts
    .map((post) => post._id)
    .filter(nonFalsey)
    .join(':');

  const selectedPost = pinnedPosts.find((post) => post._id === selectedPostId);

  // 2 states are needed so that the dialog closes and then the post is unselected, to avoid jitter
  useEffect(() => {
    if (selectedPost) {
      setPostDialogOpen(true);
    } else {
      // if a user unpins / deletes a post, make sure we cleanup
      setSelectedPostId(null);
      setPostDialogOpen(false);
    }
  }, [selectedPost]);

  if (pinnedPosts.length === 0) {
    return null;
  }

  return (
    <>
      <ListSubheader disableSticky>Pinned posts</ListSubheader>
      <Flipper flipKey={flipKey}>
        <Grid container spacing={3} paddingBottom={2}>
          {pinnedPosts.map((post) => (
            <Flipped key={post?._id} flipId={post?._id ?? ''}>
              <Grid size={{ xs: 12, md: 6 }}>
                <PinnedPostSummary
                  post={post}
                  selected={selectedPostId === post._id}
                  setSelectedPostId={setSelectedPostId}
                />
              </Grid>
            </Flipped>
          ))}
        </Grid>
      </Flipper>
      <ListSubheader disableSticky>New posts</ListSubheader>
      <Or2Dialog
        fullScreenThreshold="sm"
        aria-label="selected-post"
        title={'Pinned post - ' + selectedPost?.title}
        removeHeaderPadding
        open={postDialogOpen}
        onClose={() => setPostDialogOpen(false)}
        hasLoadingBar={false}
        slotProps={{
          transition: { onExited: () => setSelectedPostId(null) },
          content: {
            sx: {
              padding: 0,
              margin: 0,
            },
          },
        }}
      >
        {selectedPost && (
          <Post
            post={selectedPost}
            group={group}
            getSubgroupColor={getSubgroupColor}
          />
        )}
      </Or2Dialog>
    </>
  );
};
